import { Link } from 'react-router-dom';
import { FEATURE_LABELS, formatCost, formatCostPer, type FeatureUsage, type LaunchUsage, type PersonUsage } from '@/lib/ai-usage';

const TABLE = 'overflow-x-auto rounded-lg border border-domino-cardBorder bg-domino-white';
const HEAD = 'whitespace-nowrap px-3 py-2 font-medium';
const CELL = 'whitespace-nowrap px-3 py-2.5 tabular-nums';
const ROW = 'border-t border-domino-cardBorder';

const sum = (values: Array<number | null>): number => values.reduce<number>((total, value) => total + (value ?? 0), 0);

const dateLabel = (iso: string): string =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

export const FeatureTable = ({ features, canSeeCost }: { features: FeatureUsage[]; canSeeCost: boolean }) => (
  <div className={TABLE}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-domino-inkSoft">
          <th className={HEAD}>Usage</th>
          <th className={HEAD}>Nombre</th>
          {canSeeCost && <th className={HEAD}>Coût</th>}
        </tr>
      </thead>
      <tbody>
        {features.map((row) => (
          <tr key={row.feature} className={ROW}>
            <td className="px-3 py-2.5 text-domino-ink">{FEATURE_LABELS[row.feature]}</td>
            <td className={CELL}>{row.operations}</td>
            {canSeeCost && <td className={CELL}>{formatCost(row.costMicroUsd)}</td>}
          </tr>
        ))}
        {canSeeCost && (
          <tr className={ROW}>
            <td className="px-3 py-2.5 font-semibold text-domino-ink">Total</td>
            <td />
            <td className={`${CELL} font-semibold`}>{formatCost(sum(features.map((row) => row.costMicroUsd)))}</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export const PeopleTable = ({ people, canSeeCost }: { people: PersonUsage[]; canSeeCost: boolean }) => (
  <div className={TABLE}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-domino-inkSoft">
          <th className={HEAD}>Personne</th>
          <th className={HEAD}>Lancements</th>
          <th className={HEAD}>Prochaines actions</th>
          <th className={HEAD}>Messages</th>
          {canSeeCost && <th className={HEAD}>Coût</th>}
        </tr>
      </thead>
      <tbody>
        {people.map((person) => (
          <tr key={person.userId} className={ROW}>
            <td className="px-3 py-2.5 font-semibold text-domino-ink">{person.name}</td>
            <td className={CELL}>{person.launches || ''}</td>
            <td className={CELL}>{person.nextActions || ''}</td>
            <td className={CELL}>{person.messages || ''}</td>
            {canSeeCost && <td className={CELL}>{formatCost(person.costMicroUsd)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const LaunchTable = ({ launches, canSeeCost }: { launches: LaunchUsage[]; canSeeCost: boolean }) => (
  <div className={TABLE}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs text-domino-inkSoft">
          <th className={HEAD}>Outil</th>
          <th className={HEAD}>Personne</th>
          <th className={HEAD}>Date</th>
          <th className={HEAD}>Fiches</th>
          {canSeeCost && (
            <>
              <th className={HEAD}>Lancement</th>
              <th className={HEAD}>Suites</th>
              <th className={HEAD}>Par fiche</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {launches.map((launch) => (
          <tr key={launch.executionId} className={ROW}>
            <td className="px-3 py-2.5">
              <Link to={`/executions/${launch.executionId}`} className="font-semibold text-domino-aubergine hover:underline">
                {launch.title}
              </Link>
              {launch.isTest && <span className="text-domino-inkSoft"> · test</span>}
            </td>
            <td className="px-3 py-2.5 text-domino-ink">{launch.name}</td>
            <td className={CELL}>{dateLabel(launch.startedAt)}</td>
            <td className={CELL}>{launch.records || ''}</td>
            {canSeeCost && (
              <>
                <td className={CELL}>{formatCost(launch.ownCostMicroUsd)}</td>
                <td className={CELL}>{formatCost(launch.followOnCostMicroUsd)}</td>
                <td className={CELL}>
                  {formatCostPer(sum([launch.ownCostMicroUsd, launch.followOnCostMicroUsd]), launch.records)}
                </td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
